"use client";

import { cn } from "@/lib/utils";
import dayjs from "dayjs";
import { Clock4Icon } from "lucide-react";
import { useEffect, useState } from "react";

function calculateTimeLeft(activeTo: Date): number {
  const now = dayjs();
  const end = dayjs(activeTo);
  const diff = end.diff(now, "seconds");
  return diff > 0 ? diff : 0;
}

function getDaysLeft(seconds: number): number {
  return Math.floor(seconds / 86400);
}

function formatLessThanOneDay(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

function formatTimeLeft(seconds: number): string {
  const days = getDaysLeft(seconds);

  if (days > 1) {
    return `${days} dni`;
  }

  if (days === 1) {
    return `1 dzień`;
  }

  return formatLessThanOneDay(seconds);
}

const moreThanOneDayClass =
  "bg-[#F5F5FF] text-remedium-blue [&_stroke]:stroke-remedium-blue";
const lessThanOneDayClass = "bg-rose-50 text-red-500 [&_stroke]:stroke-red-500";

export const ActiveToTimer = ({ activeTo }: { activeTo: Date }) => {
  const daysLeft = getDaysLeft(calculateTimeLeft(activeTo));
  const [timeLeft, setTimeLeft] = useState<number | null>(
    calculateTimeLeft(activeTo),
  );

  useEffect(() => {
    if (daysLeft < 1) {
      const timer = setInterval(() => {
        setTimeLeft((oldTimeLeft) => Math.max(Number(oldTimeLeft) - 1, 0));
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [activeTo]);

  if (timeLeft === null || daysLeft > 14) {
    return <></>;
  }

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-sm font-bold",
        daysLeft > 1 ? moreThanOneDayClass : lessThanOneDayClass,
        daysLeft < 1 && "min-w-[100px]",
      )}
    >
      <Clock4Icon className="h-4 w-4 flex-shrink-0 font-bold" strokeWidth={3} />
      <span>
        {daysLeft < 1
          ? formatLessThanOneDay(timeLeft)
          : formatTimeLeft(timeLeft)}
      </span>
    </div>
  );
};
